import { useEffect, useRef } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";
import { useInView } from "react-intersection-observer";

interface Reference {
  name: string;
  url: string;
}

function ReferencesBlockNodeView({ node }: NodeViewProps) {
  const references: Reference[] = node.attrs.references || [];
  
  // Filter out empty references
  const validReferences = references.filter(ref => ref.name.trim() && ref.url.trim());

  // ⚠️ ANALYTICS: Inline section visibility tracking
  const hasTrackedView = useRef(false);
  const { ref: visibilityRef, inView } = useInView({ threshold: 0.5, triggerOnce: true });

  useEffect(() => {
    if (inView && !hasTrackedView.current && validReferences.length > 0) {
      hasTrackedView.current = true;
      trackEvent(ANALYTICS_EVENTS.SECTION_VIEWED, {
        section_id: 'article-references',
        section_name: 'Article References',
        section_page: 'Article',
        section_position: 95,
        reference_count: validReferences.length,
      });
    }
  }, [inView]);

  if (validReferences.length === 0) {
    return null;
  }

  return (
    <NodeViewWrapper className="references-block-wrapper" data-type="references-block">
      <div ref={visibilityRef} className="references-block">
        <div className="references-block-title">References</div>
        <ol className="references-list text-xs">
          {validReferences.map((ref, index) => (
            <li key={index} className="flex items-baseline gap-1">
              <span className="shrink-0">{index + 1}.</span>
              <a 
                href={ref.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="references-link truncate"
                onClick={() => trackEvent(ANALYTICS_EVENTS.REFERENCE_LINK_CLICKED, {
                  reference_name: ref.name,
                  reference_url: ref.url,
                  reference_index: index,
                  article_url: typeof window !== 'undefined' ? window.location.pathname : '',
                })}
              >
                {ref.name}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </NodeViewWrapper>
  );
}

export interface ReferencesBlockNodeOptions {
  HTMLAttributes: Record<string, any>;
}

export const ReferencesBlockNode = Node.create<ReferencesBlockNodeOptions>({
  name: "referencesBlock",

  group: "block",

  atom: true,

  selectable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      references: {
        default: [],
        parseHTML: (element) => {
          const data = element.getAttribute("data-references");
          if (data) {
            try {
              return JSON.parse(data);
            } catch {
              return [];
            }
          }
          return [];
        },
        renderHTML: (attributes) => {
          return { "data-references": JSON.stringify(attributes.references) };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-type='references-block']",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, {
        "data-type": "references-block",
        "data-references": JSON.stringify(HTMLAttributes.references),
        class: "references-block",
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ReferencesBlockNodeView);
  },
});

export default ReferencesBlockNode;
